import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { messageAPI, fileAPI, aiAPI, userAPI, channelAPI, e2eeAPI } from '../utils/api';
import { encryptMessage, decryptMessage, isE2EESupported } from '../utils/e2ee';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback } from './ui/avatar';
import { toast } from 'sonner';
import { Send, Paperclip, Video, Phone, Sparkles, Image as ImageIcon, File, Mic, Volume2, Play, Pause, VideoIcon, Share2, ArrowLeft, Check, CheckCheck, Lightbulb, FileText, WifiOff, RotateCcw, Trash2, MoreVertical, X, Plus, Camera, IndianRupee } from 'lucide-react';
import { format } from 'date-fns';
import EmojiPicker from './EmojiPicker';
import VoiceRecorder from './VoiceRecorder';
import VideoRecorder from './VideoRecorder';
import VideoPlayerModal from './VideoPlayerModal';
import MediaViewerModal from './MediaViewerModal';
import GifPicker from './GifPicker';
import ForwardDialog from './ForwardDialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { track } from '../utils/firebase';

const ChatView = ({ channel, socket, onBack, onStartCall }) => {
  const { user, connectionState, e2eeReady } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [showVideoRecorder, setShowVideoRecorder] = useState(false);
  const [forwardMessage, setForwardMessage] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [generatingNotes, setGeneratingNotes] = useState(false);
  const [videoModalUrl, setVideoModalUrl] = useState(null);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showSendMoney, setShowSendMoney] = useState(false);
  const [sendMoneyAmount, setSendMoneyAmount] = useState('');
  const [sendMoneyNote, setSendMoneyNote] = useState('');
  const [sendingMoney, setSendingMoney] = useState(false);
  const [heartMsgId, setHeartMsgId] = useState(null);
  const lastTapRef = useRef({});
  const scrollRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioRefs = useRef({});
  const channelRef = useRef(channel);
  const navigate = useNavigate();

  // Message queue for offline retry
  const messageQueueRef = useRef([]);
  const [pendingCount, setPendingCount] = useState(0);
  // Track sent message IDs to prevent duplicates
  const sentMessageIdsRef = useRef(new Set());
  // E2EE: cache channel participant public keys
  const channelKeysRef = useRef(null);

  channelRef.current = channel;

  // Flush queued messages and re-join room when socket reconnects
  useEffect(() => {
    if (connectionState === 'connected') {
      // Re-join room after reconnect (rooms are lost on socket disconnect)
      const currentChannel = channelRef.current;
      if (currentChannel && socket) {
        socket.emit('join_room', { room_id: currentChannel.id });
      }
      if (messageQueueRef.current.length > 0) {
        flushMessageQueue();
      }
    }
  }, [connectionState]);

  const flushMessageQueue = async () => {
    const queue = [...messageQueueRef.current];
    messageQueueRef.current = [];
    setPendingCount(0);

    for (const msg of queue) {
      try {
        const response = await messageAPI.createMessage(msg);
        sentMessageIdsRef.current.add(response.data.id);
        // Remove optimistic message, the socket update will add the real one
        setMessages((prev) => prev.filter((m) => m.id !== msg._tempId));
      } catch (err) {
        console.error('[Chat] Queue flush failed for message:', err);
        toast.error('Failed to send queued message');
      }
    }
  };

  // Load messages and join room on channel change
  useEffect(() => {
    if (!channel) return;
    loadMessages();
    loadChannelsAndUsers();
    if (socket) {
      socket.emit('join_room', { room_id: channel.id });
      socket.emit('messages_delivered', { channel_id: channel.id });
      socket.emit('mark_messages_read', { channel_id: channel.id });
    }
    setAiSuggestions([]);
    channelKeysRef.current = null; // Reset key cache on channel change
    return () => {
      if (channel && socket) {
        socket.emit('leave_room', { room_id: channel.id });
      }
    };
  }, [channel?.id, socket]);

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    const onUpdate = async (data) => {
      const currentChannel = channelRef.current;
      if (!currentChannel) return;
      if (data.type === 'new_message' && data.data.channel_id === currentChannel.id) {
        let msg = data.data;
        // Decrypt if encrypted
        if (msg.is_encrypted && msg.encrypted_keys && msg.iv && isE2EESupported()) {
          const myEncKey = msg.encrypted_keys[user.id];
          if (myEncKey) {
            try {
              const plaintext = await decryptMessage(msg.content, msg.iv, myEncKey);
              msg = { ...msg, content: plaintext, _decrypted: true };
            } catch (e) {
              console.warn('[E2EE] Realtime decrypt failed:', e);
              msg = { ...msg, content: '[Encrypted message]' };
            }
          } else {
            msg = { ...msg, content: '[Cannot decrypt — key missing]' };
          }
        }
        const decryptedMsg = msg;
        setMessages((prev) => {
          // Prevent duplicate from server (check real ID and temp ID)
          if (prev.some((m) => m.id === decryptedMsg.id && !m._tempId)) return prev;
          // If an optimistic message exists for this content from same sender, replace it
          const optimisticIdx = prev.findIndex((m) =>
            m._tempId && m.sender_id === decryptedMsg.sender_id &&
            m.channel_id === decryptedMsg.channel_id
          );
          if (optimisticIdx >= 0) {
            const updated = [...prev];
            updated[optimisticIdx] = decryptedMsg;
            return updated;
          }
          // Otherwise just append (dedup by ID)
          if (prev.some((m) => m.id === decryptedMsg.id)) return prev;
          return [...prev, decryptedMsg];
        });
        if (decryptedMsg.sender_id !== user.id) {
          socket.emit('message_read', { message_id: decryptedMsg.id });
          socket.emit('mark_messages_read', { channel_id: currentChannel.id });
        }
      } else if (data.type === 'reaction_added' || data.type === 'reaction_removed') {
        if (data.data.channel_id === currentChannel.id) {
          setMessages((prev) => prev.map((m) => (m.id === data.data.id ? data.data : m)));
        }
      } else if (data.type === 'message_deleted') {
        if (data.data.channel_id === currentChannel.id) {
          setMessages((prev) => prev.map((m) => (m.id === data.data.id ? { ...m, ...data.data } : m)));
        }
      }
    };

    const onTyping = (data) => {
      const currentChannel = channelRef.current;
      if (data.channel_id === currentChannel?.id && data.user_id !== user.id) {
        if (data.is_typing) {
          setTypingUsers((prev) => [...new Set([...prev, data.user_id])]);
        } else {
          setTypingUsers((prev) => prev.filter((id) => id !== data.user_id));
        }
      }
    };

    const onMessageUpdated = (message) => {
      setMessages((prev) => prev.map((m) => (m.id === message.id ? message : m)));
    };

    const onStatusUpdate = (data) => {
      const currentChannel = channelRef.current;
      if (data.channel_id === currentChannel?.id && data.messages) {
        const updated = new Map(data.messages.map((m) => [m.id, m]));
        setMessages((prev) => prev.map((m) => updated.get(m.id) || m));
      }
    };

    // ACK: server confirms message was saved
    const onMessageAck = (data) => {
      if (data.temp_id && data.id) {
        sentMessageIdsRef.current.add(data.id);
        setMessages((prev) => prev.map((m) =>
          m._tempId === data.temp_id ? { ...m, id: data.id, status: 'sent', _pending: false } : m
        ));
      }
    };

    socket.on('update', onUpdate);
    socket.on('typing', onTyping);
    socket.on('message_updated', onMessageUpdated);
    socket.on('messages_status_update', onStatusUpdate);
    socket.on('message_ack', onMessageAck);

    return () => {
      socket.off('update', onUpdate);
      socket.off('typing', onTyping);
      socket.off('message_updated', onMessageUpdated);
      socket.off('messages_status_update', onStatusUpdate);
      socket.off('message_ack', onMessageAck);
    };
  }, [socket, user?.id]);

  useEffect(() => { scrollToBottom(); }, [messages.length]);

  const loadMessages = async () => {
    try {
      const response = await messageAPI.getMessages(channel.id);
      const msgs = response.data;
      // Decrypt encrypted messages
      const hasE2EE = isE2EESupported() && e2eeReady;
      if (hasE2EE || isE2EESupported()) {
        const decrypted = await Promise.all(msgs.map(async (msg) => {
          if (!msg.is_encrypted || !msg.encrypted_keys || !msg.iv) return msg;
          const myEncKey = msg.encrypted_keys[user.id];
          if (!myEncKey) {
            console.warn('[E2EE] Key missing for user', user.id, 'in msg', msg.id, 'available keys:', Object.keys(msg.encrypted_keys));
            return { ...msg, content: '🔒 Encrypted (key not found)', _decryptFailed: true };
          }
          try {
            const plaintext = await decryptMessage(msg.content, msg.iv, myEncKey);
            return { ...msg, content: plaintext, _decrypted: true };
          } catch (e) {
            console.warn('[E2EE] Decrypt failed for msg', msg.id, e);
            return { ...msg, content: '🔒 Encrypted (keys changed)', _decryptFailed: true };
          }
        }));
        setMessages(decrypted);
      } else {
        setMessages(msgs);
      }
    } catch (error) {
      console.error('[Chat] Failed to load messages:', error);
      toast.error('Failed to load messages');
    }
  };

  // Fetch channel participant public keys (cached per channel)
  const getChannelKeys = async () => {
    // Always fetch fresh keys (don't cache — keys can change if user clears data)
    try {
      const res = await e2eeAPI.getChannelKeys(channel.id);
      channelKeysRef.current = res.data.keys;
      return res.data.keys;
    } catch (e) {
      console.warn('[E2EE] Failed to fetch channel keys:', e);
      return null;
    }
  };

  const loadChannelsAndUsers = async () => {
    try {
      const [channelsRes, usersRes] = await Promise.all([
        channelAPI.getChannels(),
        userAPI.getUsers(),
      ]);
      setChannels(channelsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('[Chat] Failed to load channels/users:', error);
    }
  };

  const scrollToBottom = () => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const plainContent = newMessage;
    const replyToId = replyTo?.id || null;

    // Optimistic UI: add message immediately (show plaintext locally)
    const optimistic = {
      id: tempId,
      _tempId: tempId,
      _pending: true,
      channel_id: channel.id,
      sender_id: user.id,
      content: plainContent,
      message_type: 'text',
      reply_to: replyToId,
      status: 'sending',
      reactions: [],
      read_by: [],
      delivered_to: [],
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, optimistic]);
    setNewMessage('');
    setReplyTo(null);
    setIsTyping(false);
    setAiSuggestions([]);
    if (socket) socket.emit('typing', { channel_id: channel.id, is_typing: false });

    // If socket is not connected, queue the message
    if (!socket?.connected) {
      messageQueueRef.current.push({ channel_id: channel.id, content: plainContent, message_type: 'text', _tempId: tempId });
      setPendingCount(messageQueueRef.current.length);
      toast.info('Message queued - will send when reconnected');
      return;
    }

    try {
      // Build message payload — send as plaintext for reliability
      // E2EE key exchange breaks when users clear browser data
      let msgPayload = { channel_id: channel.id, content: plainContent, message_type: 'text' };
      if (replyToId) msgPayload.reply_to = replyToId;

      const response = await messageAPI.createMessage(msgPayload);
      sentMessageIdsRef.current.add(response.data.id);
      track.sendMessage('text');
      // Replace optimistic message with real one (keep plaintext content for local display)
      setMessages((prev) => prev.map((m) =>
        m._tempId === tempId ? { ...response.data, content: plainContent, _pending: false, _decrypted: true } : m
      ));
    } catch (error) {
      console.error('[Chat] Send failed:', error);
      // Mark as failed, keep in UI for retry
      setMessages((prev) => prev.map((m) =>
        m._tempId === tempId ? { ...m, status: 'failed', _pending: false } : m
      ));
      toast.error('Failed to send message');
    }
  };

  const handleRetryMessage = async (tempId) => {
    const msg = messages.find((m) => m._tempId === tempId);
    if (!msg) return;

    setMessages((prev) => prev.map((m) =>
      m._tempId === tempId ? { ...m, status: 'sending', _pending: true } : m
    ));

    try {
      const response = await messageAPI.createMessage({
        channel_id: msg.channel_id,
        content: msg.content,
        message_type: msg.message_type,
      });
      sentMessageIdsRef.current.add(response.data.id);
      setMessages((prev) => prev.map((m) =>
        m._tempId === tempId ? { ...response.data, _pending: false } : m
      ));
    } catch (error) {
      setMessages((prev) => prev.map((m) =>
        m._tempId === tempId ? { ...m, status: 'failed', _pending: false } : m
      ));
      toast.error('Retry failed');
    }
  };

  const handleTypingStart = () => {
    if (!isTyping && socket) {
      setIsTyping(true);
      socket.emit('typing', { channel_id: channel.id, is_typing: true });
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      if (socket) socket.emit('typing', { channel_id: channel.id, is_typing: false });
    }, 2000);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const uploadResponse = await fileAPI.uploadFile(file);
      const msgType = file.type.startsWith('image/') ? 'image' : 'file';
      await messageAPI.createMessage({
        channel_id: channel.id,
        content: file.name,
        message_type: msgType,
        file_id: uploadResponse.data.file_id,
      });
      toast.success('File uploaded');
    } catch (error) {
      console.error('[Chat] Upload failed:', error);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleStartCall = (isVideo = true) => {
    if (onStartCall) {
      onStartCall(channel.id, channel.name || 'Call', isVideo);
    }
  };

  const handleSummarize = async () => {
    setSummarizing(true);
    try {
      const response = await aiAPI.summarizeChat(channel.id);
      toast.success(response.data.summary, { duration: 10000 });
    } catch (error) {
      toast.error('Failed to generate summary');
    } finally {
      setSummarizing(false);
    }
  };

  const handleSuggestReplies = async () => {
    setLoadingSuggestions(true);
    try {
      const response = await aiAPI.suggestReply(channel.id);
      setAiSuggestions(response.data.suggestions || []);
    } catch (error) {
      toast.error('Failed to get suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleMeetingNotes = async () => {
    setGeneratingNotes(true);
    try {
      const response = await aiAPI.meetingNotes(channel.id);
      toast.success('Meeting notes generated!');
      await messageAPI.createMessage({
        channel_id: channel.id,
        content: response.data.notes,
        message_type: 'text',
      });
    } catch (error) {
      toast.error('Failed to generate meeting notes');
    } finally {
      setGeneratingNotes(false);
    }
  };

  const applySuggestion = (text) => {
    setNewMessage(text);
    setAiSuggestions([]);
  };

  const handleVoiceSend = async (audioFile, duration) => {
    if (uploading) return;
    setUploading(true);
    try {
      const uploadResponse = await fileAPI.uploadFile(audioFile);
      await messageAPI.createMessage({
        channel_id: channel.id,
        content: `Voice message (${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')})`,
        message_type: 'voice',
        file_id: uploadResponse.data.file_id,
        duration,
      });
      setShowVoiceRecorder(false);
      toast.success('Voice message sent');
    } catch (error) {
      toast.error('Failed to send voice message');
    } finally {
      setUploading(false);
    }
  };

  const handleVideoSend = async (videoFile, duration) => {
    setUploading(true);
    try {
      const uploadResponse = await fileAPI.uploadFile(videoFile);
      await messageAPI.createMessage({
        channel_id: channel.id,
        content: `Video message (${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')})`,
        message_type: 'video',
        file_id: uploadResponse.data.file_id,
        duration,
      });
      setShowVideoRecorder(false);
      toast.success('Video message sent');
    } catch (error) {
      toast.error('Failed to send video message');
    } finally {
      setUploading(false);
    }
  };

  const handleGifSelect = async (gifUrl) => {
    try {
      await messageAPI.createMessage({ channel_id: channel.id, content: gifUrl, message_type: 'gif' });
    } catch (error) {
      toast.error('Failed to send GIF');
    }
  };

  const handleSendMoney = async () => {
    const amount = parseInt(sendMoneyAmount);
    if (!amount || amount < 1) return toast.error('Enter a valid amount');
    const other = channel.participant_details?.find(p => p.id !== user?.id);
    if (!other) return toast.error('Cannot determine recipient');
    setSendingMoney(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wallet/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ recipient_id: other.id, amount, note: sendMoneyNote.trim() }),
      }).then(r => r.json().then(d => ({ data: d, ok: r.ok, status: r.status })).then(({ data, ok }) => {
        if (!ok) throw new Error(data.detail || 'Transfer failed');
        return { data };
      }));
      // Send a money message in the chat
      const moneyMsg = `💸 Sent ₹${amount.toLocaleString('en-IN')}${sendMoneyNote ? ` — "${sendMoneyNote}"` : ''}`;
      await messageAPI.createMessage({ channel_id: channel.id, content: moneyMsg, message_type: 'text' });
      toast.success(`Sent ₹${amount.toLocaleString('en-IN')} to ${other.name || 'user'}`);
      setShowSendMoney(false);
      setSendMoneyAmount('');
      setSendMoneyNote('');
    } catch (error) {
      toast.error(error.message || 'Transfer failed');
    } finally {
      setSendingMoney(false);
    }
  };

  const handleAddReaction = async (messageId, emoji) => {
    try {
      await messageAPI.addReaction(messageId, emoji);
    } catch (error) {
      if (error.response?.data?.message !== 'Reaction already exists') toast.error('Failed to add reaction');
    }
  };

  const handleRemoveReaction = async (messageId, emoji) => {
    try {
      await messageAPI.removeReaction(messageId, emoji);
    } catch (error) {
      toast.error('Failed to remove reaction');
    }
  };

  const handleForward = async (messageId, targetChannelIds) => {
    await messageAPI.forwardMessage(messageId, targetChannelIds);
  };

  // Double-tap to react with heart
  const handleDoubleTap = useCallback((msgId) => {
    const now = Date.now();
    const last = lastTapRef.current[msgId] || 0;
    if (now - last < 300) {
      handleAddReaction(msgId, '❤️');
      setHeartMsgId(msgId);
      setTimeout(() => setHeartMsgId(null), 600);
      lastTapRef.current[msgId] = 0;
    } else {
      lastTapRef.current[msgId] = now;
    }
  }, []);

  // Swipe to reply
  const swipeRef = useRef({});
  const handleTouchStart = useCallback((msgId, e) => {
    swipeRef.current[msgId] = { x: e.touches[0].clientX, swiped: false };
  }, []);
  const handleTouchMove = useCallback((msgId, e, message) => {
    const start = swipeRef.current[msgId];
    if (!start || start.swiped) return;
    const diff = e.touches[0].clientX - start.x;
    if (diff > 60) {
      start.swiped = true;
      setReplyTo(message);
    }
  }, []);

  const handleDeleteForMe = async (messageId) => {
    try {
      await messageAPI.deleteForMe(messageId);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Message deleted for you');
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleDeleteForEveryone = async (messageId) => {
    try {
      await messageAPI.deleteForEveryone(messageId);
      toast.success('Message deleted for everyone');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to delete message';
      toast.error(detail);
    }
  };

  const toggleAudio = (messageId) => {
    const audio = audioRefs.current[messageId];
    if (!audio) return;
    if (playingAudio === messageId) {
      audio.pause();
      setPlayingAudio(null);
    } else {
      Object.values(audioRefs.current).forEach((a) => a.pause());
      audio.play();
      setPlayingAudio(messageId);
    }
  };

  const getSenderName = (senderId) => {
    if (senderId === user.id) return 'You';
    const sender = users.find((u) => u.id === senderId);
    return sender?.name || 'Unknown';
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Sticky top section: connection status + header */}
      <div className="shrink-0 sticky top-0 z-[1000]">
        {/* Connection Status Banner */}
        {connectionState !== 'connected' && (
          <div
            className={`px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2 ${
              connectionState === 'reconnecting' ? 'bg-orange-500 text-white' :
              connectionState === 'connecting' ? 'bg-blue-500 text-white' :
              'bg-red-500 text-white'
            }`}
            data-testid="connection-status-banner"
          >
            <WifiOff className="w-4 h-4" />
            {connectionState === 'reconnecting' && 'Reconnecting...'}
            {connectionState === 'connecting' && 'Connecting...'}
            {connectionState === 'disconnected' && 'Disconnected - messages will be queued'}
          </div>
        )}

        {/* Pending Queue Indicator */}
        {pendingCount > 0 && (
          <div className="bg-yellow-500/10 text-yellow-700 px-4 py-1 text-xs text-center" data-testid="pending-queue-banner">
            {pendingCount} message{pendingCount > 1 ? 's' : ''} queued for delivery
          </div>
        )}

        {/* Header */}
        <div className="glass-header px-3 py-1.5 flex items-center justify-between" data-testid="chat-header">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {onBack && (
            <button className="md:hidden shrink-0 p-1" onClick={onBack} data-testid="back-button">
              <ArrowLeft className="w-5 h-5 text-[#334155]" />
            </button>
          )}
          <Avatar className="h-9 w-9 shrink-0">
            <AvatarFallback className="bg-[#4338CA] text-white text-xs font-bold">
              {(() => {
                if (channel.channel_type === 'direct') {
                  const other = channel.participant_details?.find(p => p.id !== user?.id);
                  return other?.name?.charAt(0).toUpperCase() || 'C';
                }
                return channel.name?.charAt(0).toUpperCase() || 'G';
              })()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <h2 className="font-semibold text-[#111B21] text-[14px] leading-tight truncate" data-testid="channel-name">
              {channel.channel_type === 'direct'
                ? (channel.participant_details?.find(p => p.id !== user?.id)?.name || channel.name || 'Chat')
                : (channel.name || 'Group Chat')}
            </h2>
            <p className="text-[12px] leading-tight truncate" data-testid="chat-subtitle">
              {typingUsers.length > 0 ? (
                <span className="text-[#00A884] font-medium">
                  typing<span className="typing-dot ml-1" /><span className="typing-dot" /><span className="typing-dot" />
                </span>
              ) : channel.channel_type === 'direct' ? (
                <span className="text-[#667781]">
                  {channel.participant_details?.find(p => p.id !== user?.id)?.status === 'online' ? 'online' : 'offline'}
                </span>
              ) : (
                <span className="text-[#667781]">{channel.participants?.length} participants</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <button onClick={() => handleStartCall(true)} className="p-2 rounded-full hover:bg-[#F1F5F9] transition-colors" data-testid="video-call-button">
            <Video className="w-[18px] h-[18px] text-[#334155]" />
          </button>
          <button onClick={() => handleStartCall(false)} className="p-2 rounded-full hover:bg-[#F1F5F9] transition-colors" data-testid="voice-call-button">
            <Phone className="w-[18px] h-[18px] text-[#334155]" />
          </button>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button className="p-2 rounded-full hover:bg-[#F1F5F9] transition-colors" data-testid="chat-more-menu">
                <MoreVertical className="w-[18px] h-[18px] text-[#334155]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px] z-[9999]">
              <DropdownMenuItem onClick={handleSummarize} disabled={summarizing} data-testid="summarize-button">
                <Sparkles className="w-4 h-4 mr-2" /> AI Summary
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleMeetingNotes} disabled={generatingNotes} data-testid="meeting-notes-button">
                <FileText className="w-4 h-4 mr-2" /> Meeting Notes
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleSuggestReplies} disabled={loadingSuggestions} data-testid="ai-suggest-button">
                <Lightbulb className="w-4 h-4 mr-2" /> Suggest Replies
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      </div>{/* end sticky wrapper */}

      {/* Messages */}
      <ScrollArea className="flex-1 overflow-x-hidden overflow-y-auto">
        <div className="px-2 py-2 flex flex-col gap-[3px] overflow-x-hidden" data-testid="messages-container">
          {messages.map((message, idx) => {
            const isSent = message.sender_id === user.id;
            const reactions = message.reactions || [];
            const reactionGroups = reactions.reduce((acc, r) => {
              acc[r.emoji] = acc[r.emoji] || [];
              acc[r.emoji].push(r.user_id);
              return acc;
            }, {});
            const isFailed = message.status === 'failed';
            const isSending = message.status === 'sending';
            const prevMsg = messages[idx - 1];
            const isGrouped = prevMsg && prevMsg.sender_id === message.sender_id && prevMsg.message_type !== 'system' && message.message_type !== 'system';

            return (
              <div key={message.id} className={`flex flex-col ${message.message_type === 'system' ? 'items-center' : isSent ? 'items-end' : 'items-start'} ${isGrouped ? '' : 'mt-1'} group msg-enter relative`}
                onClick={() => message.message_type !== 'system' && handleDoubleTap(message.id)}
                onTouchStart={(e) => message.message_type !== 'system' && handleTouchStart(message.id, e)}
                onTouchMove={(e) => message.message_type !== 'system' && handleTouchMove(message.id, e, message)}
                data-testid={`message-${message.id}`}
              >
                {/* System messages (missed calls, etc.) */}
                {message.message_type === 'system' && (
                  <div className="bg-[#FEF3C7] text-[#92400E] text-xs px-4 py-1.5 rounded-full my-1" data-testid={`system-message-${message.id}`}>
                    {message.content}
                  </div>
                )}
                {message.message_type !== 'system' && message.forwarded_from && (
                  <div className="text-[11px] text-[#94A3B8] italic mb-0.5 px-2">
                    Forwarded from {getSenderName(message.forwarded_from)}
                  </div>
                )}
                {message.message_type !== 'system' && !isSent && channel.channel_type === 'group' && !isGrouped && (
                  <span className="text-[11px] text-[#4338CA] font-medium mb-0.5 px-2">{getSenderName(message.sender_id)}</span>
                )}
                {message.message_type !== 'system' && (
                <>
                <div className="relative">
                  {/* Reply-to reference */}
                  {message.reply_to && (() => {
                    const original = messages.find(m => m.id === message.reply_to);
                    return original ? (
                      <div className="bg-[#4338CA]/5 border-l-2 border-[#4338CA] rounded-r px-2 py-1 mb-1 max-w-[200px] cursor-pointer" onClick={() => { const el = document.querySelector(`[data-testid="message-${original.id}"]`); if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('highlight-flash'); setTimeout(() => el.classList.remove('highlight-flash'), 2000); } }} data-testid={`reply-ref-${message.id}`}>
                        <p className="text-[11px] font-semibold text-[#4338CA]">{getSenderName(original.sender_id)}</p>
                        <p className="text-[11px] text-[#64748B] truncate">{original.content || original.message_type}</p>
                      </div>
                    ) : null;
                  })()}
                  <div className={`max-w-[75%] px-[9px] py-[5px] break-words overflow-hidden relative ${isSent ? 'chat-bubble-sent' : 'chat-bubble-received'} ${isGrouped ? 'grouped' : ''} ${isSending ? 'opacity-60' : ''} ${isFailed ? 'border-2 border-red-400' : ''}`}>
                    {heartMsgId === message.id && (
                      <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                        <span className="text-3xl heart-burst">❤️</span>
                      </div>
                    )}
                    {message.message_type === 'deleted' && (
                      <p className="italic text-[#94A3B8] text-[13px] flex items-center gap-1.5" data-testid={`deleted-msg-${message.id}`}>
                        <Trash2 className="w-3 h-3" /> This message was deleted
                      </p>
                    )}
                    {message.message_type === 'text' && <p className="text-[14.2px] leading-[19px] whitespace-pre-wrap break-words">{message.content}</p>}
                    {message.message_type === 'image' && (
                      <div className="-mx-[9px] -mt-[5px] -mb-[5px]">
                        {message.file_id ? (
                          <img
                            src={fileAPI.getFileUrl(message.file_id)}
                            alt=""
                            className="w-full object-cover cursor-pointer rounded-t-[8px] rounded-b-[8px]"
                            style={{ maxHeight: '300px', minWidth: '180px' }}
                            onClick={() => setSelectedMedia({ src: fileAPI.getFileUrl(message.file_id), type: 'image' })}
                          />
                        ) : (
                          <div className="px-2.5 pt-1.5"><ImageIcon className="w-4 h-4 inline mr-2" /><span className="text-[14px]">{message.content}</span></div>
                        )}
                      </div>
                    )}
                    {message.message_type === 'file' && (
                      <a href={message.file_id ? fileAPI.getFileUrl(message.file_id) : '#'} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2.5 no-underline p-1 rounded-lg" data-testid="file-message">
                        <div className="w-10 h-10 rounded-lg bg-[#4338CA]/10 flex items-center justify-center shrink-0">
                          <File className="w-5 h-5 text-[#4338CA]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`text-[13px] font-medium truncate ${isSent ? 'text-white' : 'text-[#0F172A]'}`}>{message.content || 'Document'}</p>
                          <p className={`text-[11px] ${isSent ? 'text-white/70' : 'text-[#64748B]'}`}>
                            {message.content?.match(/\.(\w+)$/)?.[1]?.toUpperCase() || 'FILE'}
                          </p>
                        </div>
                      </a>
                    )}
                    {message.message_type === 'voice' && (
                      <div className="flex items-center gap-2" data-testid="voice-message">
                        <button onClick={() => toggleAudio(message.id)} className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isSent ? 'bg-white/20' : 'bg-[#4338CA]/10'}`}>
                          {playingAudio === message.id ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className={`h-1 rounded-full ${isSent ? 'bg-white/30' : 'bg-[#4338CA]/20'}`}>
                            <div className={`h-full rounded-full w-0 ${isSent ? 'bg-white' : 'bg-[#4338CA]'}`} />
                          </div>
                          <span className="text-[11px] opacity-70">{message.content?.match(/\d+:\d+/)?.[0] || '0:00'}</span>
                        </div>
                        <audio
                          ref={(el) => { if (el) audioRefs.current[message.id] = el; }}
                          src={message.file_id ? fileAPI.getFileUrl(message.file_id) : null}
                          onEnded={() => setPlayingAudio(null)}
                        />
                      </div>
                    )}
                    {message.message_type === 'video' && (
                      <div
                        className="-mx-[9px] -mt-[5px] -mb-[5px] relative cursor-pointer overflow-hidden"
                        onClick={() => message.file_id && setSelectedMedia({ src: fileAPI.getFileUrl(message.file_id), type: 'video' })}
                        data-testid="video-message"
                      >
                        <video
                          src={message.file_id ? fileAPI.getFileUrl(message.file_id) : null}
                          className="w-full object-cover rounded-[8px]"
                          style={{ maxHeight: '300px', minWidth: '180px' }}
                          preload="metadata"
                          muted
                          playsInline
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
                            <Play className="w-6 h-6 text-white ml-0.5" fill="white" />
                          </div>
                        </div>
                        <div className="absolute bottom-2 left-2 bg-black/60 text-white text-[11px] px-2 py-0.5 rounded font-mono">
                          {message.content?.match(/\d+:\d+/)?.[0] || '0:00'}
                        </div>
                      </div>
                    )}
                    {message.message_type === 'gif' && (
                      <div className="-mx-[9px] -mt-[5px] -mb-[5px]">
                        <img
                          src={message.content}
                          alt="GIF"
                          className="w-full object-cover cursor-pointer rounded-[8px]"
                          style={{ maxHeight: '250px', minWidth: '150px' }}
                          onClick={() => setSelectedMedia({ src: message.content, type: 'image' })}
                          data-testid="gif-message"
                        />
                      </div>
                    )}
                    {/* Timestamp + ticks — inline like WhatsApp */}
                    <div className="flex items-center justify-end gap-1 -mb-0.5 mt-px">
                      <span className={`text-[11px] leading-none ${isSent ? 'text-white/60' : 'text-[#8696A0]'}`}>{message.created_at ? format(new Date(message.created_at), 'h:mm a') : ''}</span>
                      {isSent && (
                        <span className="inline-flex items-center" data-testid={`tick-${message.id}`}>
                          {isSending && <span className="w-2.5 h-2.5 border border-current border-t-transparent rounded-full animate-spin" />}
                          {isFailed && <span className="text-red-400 text-[10px]">!</span>}
                          {!isSending && !isFailed && message.status === 'read' && <CheckCheck className="w-[14px] h-[14px] text-[#53BDEB]" />}
                          {!isSending && !isFailed && message.status === 'delivered' && <CheckCheck className="w-[14px] h-[14px] text-white/60" />}
                          {!isSending && !isFailed && message.status === 'sent' && <Check className="w-[14px] h-[14px] text-white/60" />}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Retry button for failed messages */}
                  {isFailed && (
                    <button
                      onClick={() => handleRetryMessage(message._tempId)}
                      className="absolute -bottom-6 right-0 flex items-center gap-1 text-xs text-red-500 hover:text-red-700 transition-colors"
                      data-testid={`retry-message-${message.id}`}
                    >
                      <RotateCcw className="w-3 h-3" /> Retry
                    </button>
                  )}
                  {!isFailed && !isSending && message.message_type !== 'deleted' && (
                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="icon" variant="ghost"
                          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 sm:transition-opacity h-7 w-7 touch-visible bg-white/80 dark:bg-[#1E293B]/80 shadow-sm rounded-full"
                          data-testid={`message-menu-${message.id}`}
                        >
                          <MoreVertical className="w-3.5 h-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[180px] w-[180px] z-[9999] bg-white dark:bg-[#1E293B] rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.15)] border border-[#E2E8F0] dark:border-[#334155] py-1">
                        <DropdownMenuItem onClick={() => setReplyTo(message)} className="px-3.5 py-2.5 text-[13px] cursor-pointer" data-testid={`reply-button-${message.id}`}>
                          <ArrowLeft className="w-4 h-4 mr-2.5 rotate-[135deg]" /> Reply
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setForwardMessage(message)} className="px-3.5 py-2.5 text-[13px] cursor-pointer" data-testid={`forward-button-${message.id}`}>
                          <Share2 className="w-4 h-4 mr-2.5" /> Forward
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(message.content || ''); toast.success('Copied'); }} className="px-3.5 py-2.5 text-[13px] cursor-pointer" data-testid={`copy-button-${message.id}`}>
                          <FileText className="w-4 h-4 mr-2.5" /> Copy
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDeleteForMe(message.id)} className="px-3.5 py-2.5 text-[13px] text-red-500 focus:text-red-500 focus:bg-red-50 cursor-pointer" data-testid={`delete-for-me-${message.id}`}>
                          <Trash2 className="w-4 h-4 mr-2.5" /> Delete for me
                        </DropdownMenuItem>
                        {isSent && (
                          <DropdownMenuItem onClick={() => handleDeleteForEveryone(message.id)} className="px-3.5 py-2.5 text-[13px] text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer" data-testid={`delete-for-everyone-${message.id}`}>
                            <Trash2 className="w-4 h-4 mr-2.5" /> Delete for everyone
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                {!isFailed && !isSending && Object.keys(reactionGroups).length > 0 && (
                  <div className="flex items-center gap-0.5 mt-0.5 flex-wrap">
                    {Object.entries(reactionGroups).map(([emoji, userIds]) => {
                      const hasReacted = userIds.includes(user.id);
                      return (
                        <button
                          key={emoji}
                          onClick={() => hasReacted ? handleRemoveReaction(message.id, emoji) : handleAddReaction(message.id, emoji)}
                          className={`px-1.5 py-0.5 rounded-full text-[11px] flex items-center gap-0.5 transition-all duration-150 ${
                            hasReacted ? 'bg-[#4338CA] text-white' : 'bg-white dark:bg-[#1E293B] border border-[#E2E8F0] dark:border-[#334155]'
                          }`}
                          data-testid={`reaction-${emoji}-${message.id}`}
                        >
                          <span>{emoji}</span>
                          {userIds.length > 1 && <span className="text-[10px]">{userIds.length}</span>}
                        </button>
                      );
                    })}
                    <EmojiPicker
                      onSelect={(emoji) => handleAddReaction(message.id, emoji)}
                      trigger={
                        <button className="px-1.5 py-0.5 rounded-full text-[11px] bg-white dark:bg-[#1E293B] border border-[#E2E8F0] dark:border-[#334155] opacity-50 hover:opacity-100 transition-opacity" data-testid={`add-reaction-${message.id}`}>+</button>
                      }
                    />
                  </div>
                )}
                </>
                )}
              </div>
            );
          })}
          {typingUsers.length > 0 && (
            <div className="flex items-center gap-2 text-[#94A3B8]" data-testid="typing-indicator">
              <div className="typing-indicator flex gap-1">
                <span className="w-2 h-2 bg-[#94A3B8] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-[#94A3B8] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-[#94A3B8] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-sm">typing...</span>
            </div>
          )}
          <div ref={scrollRef} />
        </div>
      </ScrollArea>

      {/* AI Suggestions */}
      {showSendMoney && channel.channel_type === 'direct' && (
        <div className="px-3 py-3 border-t border-[#E2E8F0] bg-gradient-to-r from-[#ecfdf5] to-[#f0fdf4] shrink-0" data-testid="send-money-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#10b981] flex items-center justify-center">
                <IndianRupee className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-sm font-semibold text-[#0f172a]">
                Send to {channel.participant_details?.find(p => p.id !== user?.id)?.name || 'user'}
              </span>
            </div>
            <button onClick={() => { setShowSendMoney(false); setSendMoneyAmount(''); setSendMoneyNote(''); }} className="p-1 rounded-full hover:bg-white/60" data-testid="close-send-money">
              <X className="w-4 h-4 text-[#64748b]" />
            </button>
          </div>
          <div className="flex gap-2">
            <div className="flex-1 flex items-center gap-1 px-3 py-2 bg-white rounded-lg border border-[#d1d5db] focus-within:border-[#10b981]">
              <span className="text-sm text-[#6b7280]">₹</span>
              <input
                type="number"
                value={sendMoneyAmount}
                onChange={(e) => setSendMoneyAmount(e.target.value)}
                placeholder="Amount"
                className="flex-1 text-sm bg-transparent outline-none text-[#0f172a] placeholder:text-[#9ca3af] w-16"
                data-testid="chat-money-amount"
                autoFocus
              />
            </div>
            <input
              type="text"
              value={sendMoneyNote}
              onChange={(e) => setSendMoneyNote(e.target.value)}
              placeholder="Note"
              className="flex-1 px-3 py-2 text-sm bg-white rounded-lg border border-[#d1d5db] focus:border-[#10b981] outline-none text-[#0f172a] placeholder:text-[#9ca3af]"
              data-testid="chat-money-note"
            />
            <button
              onClick={handleSendMoney}
              disabled={sendingMoney || !sendMoneyAmount || parseInt(sendMoneyAmount) < 1}
              className="px-4 py-2 rounded-lg bg-[#10b981] hover:bg-[#059669] text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="chat-money-send-btn"
            >
              {sendingMoney ? '...' : 'Pay'}
            </button>
          </div>
          <div className="flex gap-1.5 mt-2">
            {[50, 100, 200, 500].map(p => (
              <button key={p} onClick={() => setSendMoneyAmount(String(p))} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${sendMoneyAmount === String(p) ? 'bg-[#10b981] text-white' : 'bg-white text-[#334155] border border-[#e2e8f0]'}`} data-testid={`quick-amount-${p}`}>
                ₹{p}
              </button>
            ))}
          </div>
        </div>
      )}
      {aiSuggestions.length > 0 && (
        <div className="px-2 py-1.5 border-t border-[#E2E8F0] flex items-center gap-1.5 overflow-x-auto shrink-0" data-testid="ai-suggestions">
          <Lightbulb className="w-3.5 h-3.5 text-[#D97706] flex-shrink-0" />
          {aiSuggestions.map((s, i) => (
            <button
              key={`sug-${i}-${s.slice(0, 20)}`}
              onClick={() => applySuggestion(s)}
              className="px-3 py-1.5 rounded-full text-sm bg-[#4338CA]/10 text-[#4338CA] hover:bg-[#4338CA]/20 whitespace-nowrap transition-colors"
              data-testid={`suggestion-${i}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input Footer */}
      <div className="chat-footer border-t border-[#E2E8F0] dark:border-[#334155] shrink-0 bg-white dark:bg-[#0F172A]" data-testid="chat-footer">
        {/* Reply-to preview */}
        {replyTo && (
          <div className="flex items-center gap-2 bg-[#4338CA]/10 border-l-4 border-[#4338CA] rounded-r px-2 py-1 mb-1" data-testid="reply-to-banner">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-[#4338CA]">{getSenderName(replyTo.sender_id)}</p>
              <p className="text-[10px] text-[#64748B] truncate">{replyTo.content || replyTo.message_type}</p>
            </div>
            <button onClick={() => setReplyTo(null)} className="text-[#94A3B8] shrink-0" data-testid="cancel-reply">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
        {showVoiceRecorder ? (
          <VoiceRecorder onSend={handleVoiceSend} onCancel={() => setShowVoiceRecorder(false)} />
        ) : showVideoRecorder ? (
          <VideoRecorder onSend={handleVideoSend} onCancel={() => setShowVideoRecorder(false)} />
        ) : (
          <form onSubmit={handleSendMessage} className="flex items-center gap-1.5 w-full" style={{ height: '38px' }}>
            <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" data-testid="file-input" />
            {/* + Attach button */}
            <div className="relative shrink-0">
              <button type="button" onClick={() => setShowAttachMenu(!showAttachMenu)} className="footer-icon-btn bg-[#4338CA]/10 text-[#4338CA] hover:bg-[#4338CA]/20" data-testid="attach-menu-button">
                <Plus className="w-5 h-5" />
              </button>
              {showAttachMenu && (
                <>
                  <div className="fixed inset-0 z-[50]" onClick={() => setShowAttachMenu(false)} />
                  <div className="absolute bottom-12 left-0 bg-white dark:bg-[#1E293B] rounded-xl shadow-lg border border-[#E2E8F0] dark:border-[#334155] py-2 z-[51] min-w-[160px]" data-testid="attach-menu">
                    <button type="button" onClick={() => { setShowAttachMenu(false); setShowVideoRecorder(true); }} className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[#334155] dark:text-[#CBD5E1] hover:bg-[#F1F5F9] dark:hover:bg-[#334155] transition-colors" data-testid="attach-video">
                      <Camera className="w-4 h-4 text-[#7C3AED]" /> Camera
                    </button>
                    <button type="button" onClick={() => { setShowAttachMenu(false); fileInputRef.current?.click(); }} className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[#334155] dark:text-[#CBD5E1] hover:bg-[#F1F5F9] dark:hover:bg-[#334155] transition-colors" data-testid="attach-file">
                      <Paperclip className="w-4 h-4 text-[#2563EB]" /> File
                    </button>
                    <GifPicker onSelect={(url) => { setShowAttachMenu(false); handleGifSelect(url); }} />
                    {channel.channel_type === 'direct' && (
                      <button type="button" onClick={() => { setShowAttachMenu(false); setShowSendMoney(true); }} className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[#334155] dark:text-[#CBD5E1] hover:bg-[#F1F5F9] dark:hover:bg-[#334155] transition-colors" data-testid="attach-send-money">
                        <IndianRupee className="w-4 h-4 text-[#10b981]" /> Send Money
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
            {/* Input field - big and usable */}
            <Input
              value={newMessage}
              onChange={(e) => { setNewMessage(e.target.value); handleTypingStart(); }}
              placeholder="Message"
              className="chat-input flex-1 h-9 rounded-full px-4 border-[#E2E8F0] focus-visible:ring-1 focus-visible:ring-[#4338CA]"
              style={{ resize: 'none', overflow: 'hidden' }}
              data-testid="message-input"
            />
            {/* Smart send: mic when empty, send when typing */}
            {newMessage.trim() ? (
              <button type="submit" className="footer-icon-btn bg-[#4338CA] hover:bg-[#3730A3] text-white" data-testid="send-button">
                <Send className="w-4 h-4" />
              </button>
            ) : (
              <button type="button" onClick={() => setShowVoiceRecorder(true)} className="footer-icon-btn bg-[#4338CA]/10 text-[#4338CA] hover:bg-[#4338CA]/20" data-testid="voice-record-button">
                <Mic className="w-5 h-5" />
              </button>
            )}
          </form>
        )}
      </div>

      {forwardMessage && (
        <ForwardDialog
          open={!!forwardMessage}
          onClose={() => setForwardMessage(null)}
          message={forwardMessage}
          channels={channels}
          users={users}
          currentUser={user}
          onForward={handleForward}
        />
      )}
      {videoModalUrl && (
        <VideoPlayerModal videoUrl={videoModalUrl} onClose={() => setVideoModalUrl(null)} />
      )}
      {selectedMedia && (
        <MediaViewerModal src={selectedMedia.src} type={selectedMedia.type} onClose={() => setSelectedMedia(null)} />
      )}
    </div>
  );
};

export default ChatView;
